import { toImmutable } from 'nuclear-js';
import CommonValues from 'resources/common-values';

// Rules for proficiencies to display:
//     1. display 2 proficiencies:
//          a. first is for state
//              i. if no state proficiency exists, select default_state
//          b. second is college
//     2. if there are multiple studies for a state or college, pick based on
//          a. most recent year
//          b. alpha by display name
export function sortProficienciesYearThenAlpha(p1, p2) {
    if (p1.get('year') != p2.get('year')) {
        return p2.get('year') - p1.get('year');
    } else {
        return sortProficienciesAlpha(p1, p2);
    }
}

export function sortProficienciesAlphaThenYear(p1, p2) {
    if (p1.get('studyDisplayName') != p2.get('studyDisplayName')) {
        return sortProficienciesAlpha(p1, p2);
    } else {
        return sortProficienciesYear(p1, p2);
    }
}

export function sortProficienciesYear(p1, p2) {
    return p2.get('year') - p1.get('year');
}

export function sortProficienciesAlpha(p1, p2) {
    return p1
        .get('studyDisplayName')
        .toLowerCase()
        .localeCompare(p2.get('studyDisplayName').toLowerCase());
}

export function findFirstProficiency(proficiencies, proficiencyType) {
    return proficiencies
        ? proficiencies
              .filter((p) => p.get('studyType') === proficiencyType)
              .sort(sortProficienciesYearThenAlpha)
              .first()
        : null;
}

export function findProficiencies(proficiencies, proficiencyType, sortFunction) {
    return proficiencies
        ? proficiencies.filter((p) => p.get('studyType') === proficiencyType).sort(sortFunction)
        : null;
}

export function findProficienciesToDisplay(
    currentStateLinkingStudy,
    currentDefaultLinkingStudy,
    currentCollegeLinkingStudy
) {
    let proficienciesToDisplay = [];

    if (currentStateLinkingStudy) {
        proficienciesToDisplay.push(currentStateLinkingStudy);
    } else if (currentDefaultLinkingStudy) {
        proficienciesToDisplay.push(currentDefaultLinkingStudy);
    }

    if (currentCollegeLinkingStudy) {
        proficienciesToDisplay.push(currentCollegeLinkingStudy);
    }

    return toImmutable(proficienciesToDisplay);
}

/**
 * Given a testEvent and a selected subject this will return a list of proficiencies to display.
 * These rules are followed:
 *
 * STATE: Present the most recent state study
 * Select studyType = STATE
 * Select study with the most recent year.
 * If there is more than 1 STATE study for the most recent year then select the study based upon alphabetical priority
 * If no STATE exists then use DEFAULT_STATE (NWEA Generic Study)
 * If there is no linking study do not display a state study
 *
 * COLLEGE: Present college studies in alphabetical order
 * As of 2/23/2018 the ACT and SAT are the only college studies. This method is written to allow for future studies
 * Select studies where studyType = COLLAGE
 * Order by studyDisplayName.
 * Only display when Reading or Math tab is selected
 * Only display for students in grade 5-10
 * Only display when a college study exists for the geographic location
 * If a study is not available display "Projections not available" tooltip
 *
 * ORDERING:
 * State Study (should only be 1)
 * College Study (Alphabetical based on display name)
 *
 * @param testEvent The testEvent from which data will be extracted
 * @param selectedSubjectBid The bid of the selected subject
 * @returns {* List} containing the proficiencies to display
 */
export function getDisplayProficiencies(testEvent, selectedSubjectBid) {
    let displayProficiencies = [];
    let pushVal = (val) => {
        if (val) {
            displayProficiencies.push(val);
        }
    };

    if (testEvent) {
        const studentGrade = parseInt(testEvent.get('gradeName', 0));
        const studies = testEvent.get('studies', null);
        if (studies) {
            const stateLinkingStudy = this.findFirstProficiency(
                studies,
                CommonValues.linkingStudyTypes.stateLinkingStudy
            );
            let defaultLinkingStudy = null;
            let allCollegeLinkingStudies = null;
            let uniqueCollegeLinkingStudies = null;

            if (!stateLinkingStudy) {
                defaultLinkingStudy = this.findFirstProficiency(
                    studies,
                    CommonValues.linkingStudyTypes.defaultStateLinkingStudy
                );
            }

            if (studentGrade >= 5 && studentGrade <= 10) {
                if (
                    selectedSubjectBid === CommonValues.SUBJECT_BID_MATHEMATICS ||
                    selectedSubjectBid === CommonValues.SUBJECT_BID_READING
                ) {
                    allCollegeLinkingStudies = this.findProficiencies(
                        studies,
                        CommonValues.linkingStudyTypes.collegeLinkingStudy,
                        sortProficienciesAlphaThenYear
                    );

                    if (allCollegeLinkingStudies && allCollegeLinkingStudies.size > 0) {
                        uniqueCollegeLinkingStudies = allCollegeLinkingStudies.reduce(
                            (accumulator, currentValue) => {
                                if (
                                    accumulator[accumulator.length - 1].get('studyDisplayName').substring(0, 2) !==
                                    currentValue.get('studyDisplayName').substring(0, 2)
                                ) {
                                    accumulator.push(currentValue);
                                }
                                return accumulator;
                            },
                            [allCollegeLinkingStudies.get(0)]
                        );
                    }
                }
            }
            pushVal(stateLinkingStudy);
            pushVal(defaultLinkingStudy);
            if (uniqueCollegeLinkingStudies) {
                uniqueCollegeLinkingStudies.forEach((collegeLinkingStudy) => {
                    pushVal(collegeLinkingStudy);
                });
            }
        }
    }

    return toImmutable(displayProficiencies);
}

export default {
    findProficienciesToDisplay,
    findProficiencies,
    findFirstProficiency,
    getDisplayProficiencies,
};
